import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import {
  CloudUpload,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import auditService from '../services/auditService';

const BulkSyncDialog = ({ open, onClose }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Confirm, 3: Processing, 4: Results
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleClose = () => {
    if (!loading) {
      setStep(1);
      setSelectedFile(null);
      setError(null);
      setResults(null);
      onClose();
    }
  };

  const handleFileSelect = (file) => {
    setError(null);

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      setError('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setSelectedFile(file);
    setStep(2);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleConfirmSync = async () => {
    setStep(3);
    setLoading(true);
    setError(null);

    try {
      const result = await auditService.bulkSyncData(selectedFile);
      setResults(result);
      setStep(4);
    } catch (error) {
      console.error('Bulk sync error:', error);
      setError(error.response?.data?.error || 'An error occurred during bulk sync');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const renderUploadStep = () => (
    <>
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mb-2">
          Upload Excel Data File
        </h3>
        <p className="text-sm text-muted-foreground">
          Select an Excel file to sync product, quotation, and sales data
        </p>
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
          dragOver
            ? 'border-blue-500 bg-primary/10'
            : 'border-gray-300 bg-muted hover:bg-muted hover:border-blue-500'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-upload').click()}
      >
        <CloudUpload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h4 className="text-lg font-semibold mb-2">
          Drag and drop your Excel file here
        </h4>
        <p className="text-sm text-muted-foreground mb-2">
          or click to browse
        </p>
        <p className="text-xs text-muted-foreground">
          Supported formats: .xlsx, .xls
        </p>

        <input
          id="file-upload"
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <DialogFooter className="mt-6">
        <Button variant="outline" onClick={handleClose}>Cancel</Button>
      </DialogFooter>
    </>
  );

  const renderConfirmStep = () => (
    <>
      <div className="text-center mb-6">
        <AlertTriangle className="h-12 w-12 text-warning mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">
          Confirm Data Sync
        </h3>
        <p className="text-sm text-muted-foreground">
          This operation will replace ALL existing data
        </p>
      </div>

      <Alert variant="warning" className="mb-6 border-yellow-500 bg-warning/10">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-semibold mb-1">Warning: This action cannot be undone!</p>
          <p className="text-sm">
            All existing products, quotations, and sales orders will be permanently deleted and replaced with data from the uploaded Excel file.
          </p>
        </AlertDescription>
      </Alert>

      <div className="bg-muted p-4 rounded-lg mb-4">
        <p className="font-semibold mb-2">Selected File:</p>
        <p className="text-sm">
          <strong>Name:</strong> {selectedFile?.name}
        </p>
        <p className="text-sm">
          <strong>Size:</strong> {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <DialogFooter className="mt-6">
        <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
        <Button variant="outline" onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleConfirmSync}
          className="bg-warning/100 hover:bg-warning text-white"
          disabled={loading}
        >
          Confirm Sync
        </Button>
      </DialogFooter>
    </>
  );

  const renderProcessingStep = () => (
    <div className="text-center py-8">
      <h3 className="text-lg font-semibold mb-2">
        Processing Data Sync...
      </h3>
      <p className="text-sm text-muted-foreground mb-6">
        Please wait while we process your Excel file and update the database.
      </p>
      <Progress className="mb-4" />
      <p className="text-xs text-muted-foreground">
        This may take a few minutes depending on the file size.
      </p>
    </div>
  );

  const renderResultsStep = () => (
    <>
      <div className="text-center mb-6">
        <CheckCircle className="h-12 w-12 text-success mx-auto mb-4" />
        <h3 className="text-lg font-semibold">
          Sync Completed Successfully
        </h3>
      </div>

      {results && (
        <>
          <p className="font-semibold mb-4">Data Summary:</p>

          <Card className="p-4 mb-4">
            <h4 className="text-lg font-semibold text-destructive mb-2">
              Data Removed:
            </h4>
            <ul className="space-y-1">
              <li className="py-1">Products: {results.summary?.deleted?.products || 0}</li>
              <li className="py-1">Quotations: {results.summary?.deleted?.quotations || 0}</li>
              <li className="py-1">Sales Orders: {results.summary?.deleted?.sales_orders || 0}</li>
            </ul>
          </Card>

          <Card className="p-4">
            <h4 className="text-lg font-semibold text-success mb-2">
              Data Added:
            </h4>
            <ul className="space-y-1">
              <li className="py-1">Products: {results.summary?.inserted?.products || 0}</li>
              <li className="py-1">Quotations: {results.summary?.inserted?.quotations || 0}</li>
              <li className="py-1">Sales Orders: {results.summary?.inserted?.sales_orders || 0}</li>
            </ul>
          </Card>
        </>
      )}

      <DialogFooter className="mt-6">
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );

  const getDialogTitle = () => {
    switch (step) {
      case 1: return 'Bulk Data Sync';
      case 2: return 'Confirm Data Sync';
      case 3: return 'Processing...';
      case 4: return 'Sync Complete';
      default: return 'Bulk Data Sync';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
        </DialogHeader>

        {step === 1 && renderUploadStep()}
        {step === 2 && renderConfirmStep()}
        {step === 3 && renderProcessingStep()}
        {step === 4 && renderResultsStep()}
      </DialogContent>
    </Dialog>
  );
};

export default BulkSyncDialog;
